import dayjs from "dayjs";
import type { CandleData } from "../context";

// Based on https://linear.app/artis-works/issue/AUTHK-41/exclude-saturdays-and-sundays-market-closed
// We want to define the periods of "no trading activity" as the union of the following 5 periods:
// Friday 11pm to Sunday 10pm
// Daily overnight closed periods:
// Monday 11pm to Tuesday 12:45am
// Tuesday 11pm to Wednesday 12:45am
// Wednesday 11pm to Thursday 12:45am
// Thursday 11pm to Friday 12:45am
type ClosedPeriodRule = {
  days: number[]; // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  start: { hour: number; minute: number };
  end: { hour: number; minute: number };
};

export const CLOSED_PERIODS: ClosedPeriodRule[] = [
  // Weekend closed: Friday 23:00 to Sunday 22:00
  { days: [5], start: { hour: 23, minute: 0 }, end: { hour: 23, minute: 59 } }, // Friday after 23:00
  { days: [6], start: { hour: 0, minute: 0 }, end: { hour: 23, minute: 59 } }, // Saturday all day
  { days: [0], start: { hour: 0, minute: 0 }, end: { hour: 21, minute: 59 } }, // Sunday before 22:00

  // Daily overnight closed periods: 23:00 - 00:44 (next day)
  {
    days: [1, 2, 3, 4],
    start: { hour: 23, minute: 0 },
    end: { hour: 23, minute: 59 },
  }, // Mon-Thu after 23:00
  {
    days: [2, 3, 4, 5],
    start: { hour: 0, minute: 0 },
    end: { hour: 0, minute: 44 },
  }, // Tue-Fri before 00:45
];

/** Calculates a historical timestamp by jumping back a specific number of candles, skipping market-closed periods. */
export const calculatePastTimeWithMarketGaps = (toTime: number, secondsPerCandle: number, candles: number) => {
  let remaining = candles;
  let current = toTime;
  const stepMs = secondsPerCandle * 1000;

  while (remaining > 0) {
    current -= stepMs;
    if (!isClosedPeriod(current)) {
      remaining--;
    }
  }

  return current;
};

// Checks if a given date is within a closed period based on a rule.
function isWithinPeriod(date: dayjs.Dayjs, rule: ClosedPeriodRule): boolean {
  const day = date.day();
  if (!rule.days.includes(day)) return false;

  const time = date.hour() * 60 + date.minute();
  const start = rule.start.hour * 60 + rule.start.minute;
  const end = rule.end.hour * 60 + rule.end.minute;

  return time >= start && time <= end;
}

export function isClosedPeriodDate(date: dayjs.Dayjs): boolean {
  return CLOSED_PERIODS.some((rule) => isWithinPeriod(date, rule));
}

// Given a timestamp, i.e. 1752361200 (Sun Jul 13 2025 00:00:00 GMT+0100 (British Summer Time)), check if it is within any of the closed periods.
export function isClosedPeriod(timestamp: number): boolean {
  return isClosedPeriodDate(dayjs(timestamp));
}

// Given a timestamp, i.e. 1752361200 (Sun Jul 13 2025 00:00:00 GMT+0100 (British Summer Time)), get the closest open period looking backwards, so Fri Jul 11 2025 22:00:00 GMT+0100 (British Summer Time) in this case (with some padding to avoid the weekend closed period). Return a timestamp.
export function closestOpenPeriod(timestamp: number): number {
  if (!isClosedPeriod(timestamp)) return timestamp;
  const date = dayjs(timestamp);

  // If it is in a closed period, we need to find the last open period. The calculation is cheap, so just subtract 2 hours and check again, up to 72 hours (3 days).
  // This has been tested and runs in 0.1 - 0.2 ms on average.
  for (let i = 2; i <= 72; i += 2) {
    const previousDate = date.subtract(i, "hours");
    if (!isClosedPeriod(previousDate.valueOf())) {
      return previousDate.valueOf();
    }
  }

  // If we reach here, just return where we reached.
  return date.valueOf();
}

export function closestCandleToTime(candles: CandleData[], timestamp: number) {
  if (candles.length === 0) return null;

  // Look backwards through candles until we find one that is before or equal to the target time.
  let closest: CandleData | null = null;
  for (let i = candles.length - 1; i >= 0; i--) {
    if ((candles[i].time as number) <= timestamp) {
      closest = candles[i];
      break;
    }
  }

  return closest;
}
